import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, SyntheticChip } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { CheckCircle2 } from "lucide-react";

const pct = (n) => `${Math.round(n * 100)}%`;

export default function Analytics() {
  const [a, setA] = useState(null);
  useEffect(() => { api.analytics().then(setA).catch(() => {}); }, []);
  if (!a) return <div className="text-sm text-slate-500">Loading analytics…</div>;
  const m = a.prototype_metrics;
  const provData = a.provider_readiness.map((p) => ({ name: p.provider.split(" ")[0], ready: Math.round(p.ready_rate * 100) }));

  return (
    <div>
      <PageHeader testid="analytics-header" title="Readiness Analytics"
        subtitle="Prototype metrics computed from synthetic data. Financial KPIs require real-world validation." />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Metric label="Orders Evaluated" value={m.orders_evaluated} />
        <Metric label="READY Rate" value={pct(m.ready_rate)} />
        <Metric label="ACTION NEEDED Rate" value={pct(m.action_needed_rate)} />
        <Metric label="REVIEW REQUIRED Rate" value={pct(m.review_required_rate)} />
        <Metric label="First-Submission Readiness" value={pct(m.first_submission_readiness)} />
        <Metric label="Clarification Volume" value={m.clarification_volume} />
        <Metric label="Avg Resolution (hrs)" value={m.avg_synthetic_resolution_hours} />
        <Metric label="Override Frequency" value={m.override_frequency} />
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        <Card className="p-4" data-testid="provider-readiness">
          <div className="mb-1 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-800">Provider Readiness</div>
            <SyntheticChip label="Illustrative future analytics" />
          </div>
          <p className="mb-3 text-xs text-slate-500">Illustrative future analytics generated from synthetic data. Tests whether provider-level insight is valuable to lab leaders.</p>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={provData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" fontSize={12} /><YAxis fontSize={11} domain={[0, 100]} /><Tooltip />
              <Bar dataKey="ready" fill="#0d9488" radius={[4, 4, 0, 0]} name="% READY first submission" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-3 space-y-1.5">
            {a.provider_readiness.map((p) => (
              <div key={p.provider} className="flex items-center justify-between rounded border border-slate-200 p-2 text-xs">
                <span className="font-medium text-slate-700">{p.provider}</span>
                <span className="text-slate-500">{pct(p.ready_rate)} READY · {p.common_issue === "None" ? "no common issue" : `common: ${p.common_issue}`}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-4">
          <div className="mb-1 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-800">Future Validated KPIs</div>
            <SyntheticChip label="Not available yet" />
          </div>
          <p className="mb-3 text-xs text-slate-500">These require controlled real-world validation before they can be reported.</p>
          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {a.future_kpis.map((k) => (
              <div key={k.name} className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-2.5 text-xs">
                <div className="font-medium text-slate-700">{k.name}</div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700">{k.note}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

const Metric = ({ label, value }) => (
  <Card className="p-4"><div className="font-display text-2xl font-bold text-slate-900">{value}</div><div className="text-xs text-slate-500">{label}</div></Card>
);
